import React, { useEffect, useState } from 'react';
import { Card, Table, Form, Input, Switch, Button, Space, App, Alert, Tag, Modal } from 'antd';
import { SaveOutlined, ReloadOutlined, EditOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

/**
 * TicketConfigPage — cấu hình kết nối TicketSupportSystem cho TỪNG MODULE.
 *
 * Mỗi module FMS đăng ký là một SoftwareProduct riêng bên TicketSystem ⇒ mỗi dòng
 * một BasicAuthKey riêng (TicketSystem validate cả org_code LẪN product_code phải
 * khớp product gắn với key đó). HACT: 3 product FMS / NRPT / PMS, org HACT.
 *
 * BasicAuthKey KHÔNG BAO GIỜ được backend trả về — chỉ có cờ HasKey. Để trống ô key
 * khi lưu = giữ nguyên key cũ (cùng khuôn với cấu hình mail của module Noon).
 */
export function TicketConfigPage({ axios }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [editing, setEditing] = useState(null);   // ProductCode đang sửa

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/ticket/config');
      setRows(r.data?.Data ?? []);
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const openEdit = (row) => {
    setEditing(row.ProductCode);
    // forceRender ở Modal đảm bảo Form đã mount trước khi set giá trị.
    form.setFieldsValue({
      OrgCode:  row.OrgCode,
      OrgName:  row.OrgName,
      ApiUrl:   row.ApiUrl,
      IsActive: !!row.IsActive,
      GhiChu:   row.GhiChu ?? '',
      BasicAuthKey: '',        // luôn để trống: backend không trả key thật
    });
  };

  const save = async (values) => {
    setSaving(true);
    try {
      await axios.put(`/api/ticket/config/${encodeURIComponent(editing)}`, values);
      message.success(t('common.savedAt', 'Đã lưu lúc') + ' ' + new Date().toLocaleTimeString());
      setEditing(null);
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: t('ticketConfig.productCode', 'Mã sản phẩm'), dataIndex: 'ProductCode', width: 130,
      render: (v) => <Tag color="blue">{v}</Tag> },
    { title: t('ticketConfig.note', 'Module'),      dataIndex: 'GhiChu' },
    { title: t('ticketConfig.orgCode', 'Mã đơn vị'), dataIndex: 'OrgCode', width: 110 },
    { title: t('ticketConfig.apiUrl', 'Địa chỉ hệ thống hỗ trợ'), dataIndex: 'ApiUrl' },
    { title: t('ticketConfig.hasKey', 'Khoá kết nối'), dataIndex: 'HasKey', width: 130, align: 'center',
      render: (v) => v
        ? <Tag color="green">{t('ticketConfig.keySet', 'Đã có')}</Tag>
        : <Tag color="red">{t('ticketConfig.keyMissing', 'Chưa có')}</Tag> },
    { title: t('ticketConfig.active', 'Đang bật'), dataIndex: 'IsActive', width: 110, align: 'center',
      render: (v) => v
        ? <Tag color="green">{t('common.yes', 'Có')}</Tag>
        : <Tag>{t('common.no', 'Không')}</Tag> },
    { title: '', width: 60, align: 'center',
      render: (_, row) => <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} /> },
  ];

  return (
    <Card
      title={<Space><CustomerServiceOutlined />{t('menu.ticketConfig', 'Hệ thống hỗ trợ')}</Space>}
      extra={<Button icon={<ReloadOutlined />} onClick={refresh}>{t('button.refresh', 'Làm mới')}</Button>}
    >
      <Alert
        type="info" showIcon style={{ marginBottom: 16 }}
        message={t('ticketConfig.hint.title', 'Mỗi module là một sản phẩm riêng bên hệ thống hỗ trợ')}
        description={t('ticketConfig.hint.desc',
          'Khoá kết nối (Basic auth key) do quản trị hệ thống hỗ trợ cấp riêng cho từng mã sản phẩm. '
          + 'Chưa nhập khoá hoặc chưa bật thì nút hỗ trợ trong module đó sẽ báo chưa cấu hình.')}
      />

      <Table
        rowKey="ProductCode"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        onRow={(row) => ({ onDoubleClick: () => openEdit(row) })}
      />

      <Modal maskClosable={false}
        open={!!editing}
        title={`${t('ticketConfig.edit', 'Cấu hình')} — ${editing ?? ''}`}
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        forceRender
        okText={<Space><SaveOutlined />{t('button.save', 'Lưu')}</Space>}
        cancelText={t('button.cancel', 'Huỷ')}
      >
        <Form form={form} layout="vertical" onFinish={save} autoComplete="off">
          <Form.Item name="ApiUrl" label={t('ticketConfig.apiUrl', 'Địa chỉ hệ thống hỗ trợ')}
                     rules={[{ required: true }]}>
            <Input placeholder="https://support.vietms.vn" />
          </Form.Item>
          <Form.Item name="OrgCode" label={t('ticketConfig.orgCode', 'Mã đơn vị')}
                     rules={[{ required: true }]}>
            <Input placeholder="HACT" />
          </Form.Item>
          <Form.Item name="OrgName" label={t('ticketConfig.orgName', 'Tên đơn vị')}>
            <Input />
          </Form.Item>
          <Form.Item name="BasicAuthKey" label={t('ticketConfig.key', 'Khoá kết nối')}
                     extra={t('ticketConfig.keyHint',
                       'Để trống = giữ nguyên khoá đang lưu. Khoá không bao giờ được hiển thị lại.')}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="GhiChu" label={t('ticketConfig.note', 'Module')}>
            <Input />
          </Form.Item>
          <Form.Item name="IsActive" label={t('ticketConfig.active', 'Đang bật')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
